import { BENCHMARK_SUMMARY } from "./benchmark-profile";
import { assessCodeQuality } from "./code-quality";
import { formatBytes, openContainer } from "./archive-ingest";
import {
  type DetectionContext,
  FAMILY_MARKERS,
} from "./detection-rules";
import { assembleFindings } from "./findings-assembly";
import {
  MAX_ENTRY_HASH_BUDGET,
  MAX_NESTED_ARCHIVES,
  walkArchive,
} from "./archive-walk";
import { runDetectors } from "./detectors";
import type { AnalyzeOptions } from "./analyzer-options";
import { ensureNotAborted, reportProgress } from "./scan-runtime";
import { detectFileFormat } from "./file-formats";
import {
  calculateScore,
  identityFloor,
  verdictFor,
} from "./scoring";
import {
  detectFamilySignatures,
  getThreatIntelCounts,
  THREAT_INTEL_VERSION,
} from "./threat-intel";
import type {
  ArchiveBreakdown,
  ArchiveEntry,
  Finding,
  KnownHashMatch,
  RecoveredExpression,
  ScanProgress,
  ScanReport,
  ScanSession,
  Severity,
} from "./types";

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const ANALYZER_VERSION = "JarLab Static Engine 6.3";

function findingCounts(findings: Finding[]) {
  const counts: Record<Severity, number> = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    info: 0,
  };
  for (const finding of findings) counts[finding.severity] += 1;
  return counts;
}

function archiveBreakdown(entries: ArchiveEntry[]): ArchiveBreakdown {
  return entries.reduce<ArchiveBreakdown>(
    (result, entry) => {
      if (entry.directory) return result;
      if (entry.kind === "class") result.classes += 1;
      else if (entry.kind === "config" || entry.kind === "manifest")
        result.configs += 1;
      else if (entry.kind === "native") result.native += 1;
      else if (entry.kind === "archive") result.archives += 1;
      else result.resources += 1;
      return result;
    },
    { classes: 0, resources: 0, configs: 0, native: 0, archives: 0 },
  );
}

function familyTags(
  contexts: DetectionContext[],
  findingIds: Set<string>,
  knownHashMatches: KnownHashMatch[],
  signatureMatches: ReturnType<typeof detectFamilySignatures>,
) {
  const joined = contexts.flatMap((context) => context.values).join("\n");
  const tags = [
    ...knownHashMatches.map(
      (match) => `${match.family} · exact hash`,
    ),
    ...signatureMatches.map(
      (match) => `${match.family} · ${match.variant}`,
    ),
    ...FAMILY_MARKERS.filter(([pattern]) => pattern.test(joined)).map(
      ([, label]) => label,
    ),
  ];
  if (
    [
      "JARLAB-CRED-001",
      "JARLAB-CRED-002",
      "JARLAB-CRED-003",
      "JARLAB-CRED-004",
      "JARLAB-MC-002",
      "JARLAB-MC-003",
    ].some((id) => findingIds.has(id)) &&
    findingIds.has("JARLAB-NET-001")
  ) {
    tags.push("Stealer-like behavior");
  }
  if (
    findingIds.has("JARLAB-LOAD-001") &&
    findingIds.has("JARLAB-NET-001")
  ) {
    tags.push("Loader-like behavior");
  }
  if (findingIds.has("JARLAB-NATIVE-001")) tags.push("Native payload");
  return Array.from(new Set(tags));
}

export async function analyzeJar(
  file: File,
  onProgress?: (progress: ScanProgress) => void,
  signal?: AbortSignal,
  options: AnalyzeOptions = {},
): Promise<ScanSession> {
  // A scan is five phases, and this function is the sequence of them. Four of
  // the five are one call each into a module that owns its own state; only the
  // two ends stay here.
  //
  //   1a. read and identify     this function      what did the analyst hand us
  //   1b. open the container    archive-ingest     what is in the container
  //   2.  walk the archive      archive-walk       what is inside it
  //   3.  run the detectors    detectors          what that means
  //   4.  assemble findings    findings-assembly  which observations are findings
  //   5.  build the report     this function      the object the app renders
  //
  // The output of each phase is the input to the next, and nothing reaches past
  // a phase except the report. Phases 1a and 5 stayed here because they are the
  // ends of the pipeline: one is the signature, one is a single object literal
  // with no logic in it to name.
  //
  // Each phase hands back a record, and each record is destructured on arrival.
  // That is 64 names re-bound across four boundaries, and every one of them is
  // read again later — none is dead. Reducing the count further means changing
  // what the phases exchange, not tidying this function. See ARCHITECTURE.md.

  // -- Phase 1a: read and identify ------------------------------------------
  const started = performance.now();
  const startedAt = new Date().toISOString();
  const lowerName = file.name.toLowerCase();

  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error("This file is over JarLab’s 50 MB local scan limit.");
  }
  if (file.size < 4) throw new Error("The selected file is empty.");

  reportProgress(onProgress, "Reading file", 3, `Reading ${file.name}`);
  const uploadedBytes = new Uint8Array(await file.arrayBuffer());
  ensureNotAborted(signal);
  const uploadedFormat = detectFileFormat(uploadedBytes, file.name);

  // -- Phase 1b: open the container -----------------------------------------
  // Read the zip, unwrap a jar out of a zip-like wrapper, enforce the two
  // limits that are about the container rather than its contents. Owned by
  // `archive-ingest.ts`, which is the module that answers "what is in this
  // container" for every other part of the scan too.
  const {
    archive,
    zipObjects,
    analyzedBytes,
    analyzedFileName,
    innerArchive,
    containerType,
    containerReadWarning,
    unsafePaths,
    totalUncompressed,
  } = await openContainer({
    uploadedBytes,
    fileName: file.name,
    lowerName,
    format: uploadedFormat,
    onProgress,
    signal,
  });

  // -- Phase 2: walk the archive -------------------------------------------
  // Everything the rest of the scan knows about the contents is decided here:
  // hashes, entries, classes, strings, dependencies, entry points, nested
  // archives and decoded values. One call, one result.
  const walked = await walkArchive({
    file,
    archive,
    zipObjects,
    uploadedBytes,
    analyzedBytes,
    analyzedFileName,
    options,
    onProgress,
    signal,
  });
  const {
    sampleSha256,
    sampleSha512,
    sampleSha1,
    sampleMd5,
    analyzedSha256,
    knownHashMatches,
    officialLookupPromise,
    archiveEntries,
    archiveLayoutSha256,
    entryHashes,
    hashedEntryBytes,
    hashCandidates,
    topLevelHashedCount,
    contexts,
    classes,
    extractedStrings,
    dependencies,
    entryPoints,
    staticRecoveryBySource,
    staticRecoveredExpressions,
    remainingInstructionBudget,
    manifestRaw,
    manifest,
    stringId,
    classEntries,
    nestedArchivesInspected,
    nestedClassBudget,
    nestedCandidates,
    decodedValues,
    decodedValueCount,
  } = walked;

  // -- Phase 3: run the detectors -------------------------------------------
  // Fingerprints, data flow, actions, payloads, integrity, reputation, family
  // signatures and YARA. The reachability closures this returns are what make
  // an action reportable, so they are built here and consumed by assembly.
  const detected = await runDetectors({
    archive,
    analyzedBytes,
    analyzedFileName,
    archiveEntries,
    classes,
    contexts,
    entryPoints,
    extractedStrings,
    entryHashes,
    hashedEntryBytes,
    officialLookupPromise,
    options,
    onProgress,
    signal,
    stringId,
    decodedValueCount,
  });
  const {
    fingerprints,
    fingerprintMatches,
    symbolic,
    actions,
    behaviorChains,
    suspiciousTerms,
    hasReachabilityRoots,
    entryIsReachable,
    referencedByReachableCode,
    embeddedPayloads,
    signatureFiles,
    integrity,
    officialFile,
    familySignatureMatches,
    yaraState,
    yaraWarnings,
    yaraMatches,
  } = detected;

  // -- Phase 4: assemble the findings ---------------------------------------
  // Every rule runs here, in the order the scan produced its inputs, and the
  // capability gate that decides whether suspicious code moves the verdict.
  reportProgress(
    onProgress,
    "Running detections",
    69,
    "Matching behavior and secret rules",
  );
  const {
    findings,
    indicators,
    dependencyAdvisories,
    advisoryUnchecked,
    advisoryDataset,
    advisoryDatasetError,
    advisoryWarnings,
    capabilityProven,
  } = await assembleFindings({
    contexts,
    actions,
    classes,
    entryPoints,
    archiveEntries,
    unsafePaths,
    dependencies,
    signatureFiles,
    knownHashMatches,
    decodedValues,
    staticRecoveryBySource,
    symbolic,
    embeddedPayloads,
    integrity,
    officialFile,
    yaraMatches,
    fingerprintMatches,
    familySignatureMatches,
    analyzedFileName,
    entryIsReachable,
    referencedByReachableCode,
    hasReachabilityRoots,
  });

  // -- Phase 5: build the report --------------------------------------------
  // One object literal, and deliberately not a module: a report contains
  // everything the scan observed, so its inputs are the whole pipeline, and
  // a function taking 45 of them would be a bag rather than a boundary.
  reportProgress(
    onProgress,
    "Building report",
    93,
    "Correlating findings and archive metadata",
  );

  const score = calculateScore(
    findings,
    capabilityProven,
    identityFloor(knownHashMatches, familySignatureMatches, fingerprintMatches),
  );
  const completedAt = new Date().toISOString();
  const compressedTotal = archiveEntries.reduce(
    (sum, entry) => sum + entry.compressedSize,
    0,
  );
  const recoveredExpressionIndex = new Map<string, RecoveredExpression>();
  for (const item of [...staticRecoveredExpressions, ...symbolic.recovered]) {
    const key = `${item.entry}\u0000${item.method}\u0000${item.offset}\u0000${item.value}`;
    const previous = recoveredExpressionIndex.get(key);
    if (
      !previous ||
      Number(item.patchable) > Number(previous.patchable) ||
      (item.confidence === "high" && previous.confidence !== "high")
    ) {
      recoveredExpressionIndex.set(key, item);
    }
  }
  const recoveredExpressions = Array.from(
    recoveredExpressionIndex.values(),
  ).slice(0, 3_000);
  const codeQuality = assessCodeQuality(
    classes,
    extractedStrings,
    fingerprints.methods,
  );
  // `nestedCandidates` is every archive entry the walk considered, before the
  // cap. Split the shortfall by cause: what the cap alone prevented, and what
  // the walk tried and could not open (too large, malformed, over budget).
  const nestedAttempted = Math.min(
    nestedCandidates.length,
    MAX_NESTED_ARCHIVES,
  );
  const nestedCapSkipped = nestedCandidates.length - nestedAttempted;
  const nestedSkippedAfterCap = nestedAttempted - nestedArchivesInspected;

  const report: ScanReport = {
    id: sampleSha256.slice(0, 12),
    fileName: file.name,
    analyzedFileName,
    fileSize: file.size,
    analyzedSize: analyzedBytes.byteLength,
    containerType,
    innerArchive,
    hashes: {
      sha256: sampleSha256,
      sha512: sampleSha512,
      sha1: sampleSha1,
      md5: sampleMd5,
      analyzedSha256,
      archiveLayoutSha256,
    },
    startedAt,
    completedAt,
    durationMs: Math.max(1, Math.round(performance.now() - started)),
    score,
    verdict: verdictFor(score),
    counts: findingCounts(findings),
    findings,
    actions,
    behaviorChains,
    dataFlows: symbolic.traces,
    recoveredExpressions,
    crossReferences: symbolic.references,
    indicators,
    entries: archiveEntries,
    classes,
    entryPoints: Array.from(
      new Map(
        entryPoints.map((entryPoint) => [
          `${entryPoint.type}:${entryPoint.className}:${entryPoint.environment ?? ""}`,
          entryPoint,
        ]),
      ).values(),
    ),
    strings: extractedStrings
      .sort(
        (left, right) =>
          Number(right.suspicious) - Number(left.suspicious) ||
          left.value.localeCompare(right.value),
      )
      .slice(0, 4_000),
    suspiciousTerms,
    dependencies,
    manifest,
    manifestRaw,
    signed: integrity.status === "entry-digests-valid",
    signatureFiles,
    integrity,
    officialFile,
    familyTags: familyTags(
      contexts,
      new Set(findings.map((item) => item.ruleId)),
      knownHashMatches,
      familySignatureMatches,
    ),
    knownHashMatches,
    fingerprintMatches,
    yaraMatches,
    yaraUnsupported: yaraState.unsupported,
    yaraRulesLoaded: yaraState.rules.length,
    yaraGeneration: yaraState.generation,
    advisoryDataset,
    advisoryDatasetError,
    advisoryUnchecked,
    dependencyAdvisories,
    familyMatches: familySignatureMatches,
    entryHashes: entryHashes.sort((left, right) =>
      left.path.localeCompare(right.path),
    ),
    nestedArchivesInspected,
    decodedValueCount,
    methodFingerprints: fingerprints.methods,
    classFingerprints: fingerprints.classes,
    archiveFingerprint: fingerprints.archive,
    embeddedPayloads,
    codeQuality,
    coverage: {
      parsedClasses: classes.length,
      totalClassEntries: classEntries.length,
      decodedInstructions: fingerprints.archive.instructionCount,
      truncatedMethods: classes.reduce(
        (total, classInfo) =>
          total +
          classInfo.methods.filter((method) => method.bytecodeTruncated).length,
        0,
      ),
      evaluatedMethods: symbolic.evaluatedMethods,
      reachableMethods: symbolic.reachableMethods,
      dataFlowTraces: symbolic.traces.length,
      payloadsInspected: embeddedPayloads.length,
      entriesIntegrityChecked: integrity.verifiedEntryCount,
      limitsHit: [
        ...(remainingInstructionBudget === 0
          ? ["Global JVM instruction budget reached"]
          : []),
        ...(entryHashes.length >= 4_000
          ? ["Entry-hash count limit reached"]
          : []),
        ...(extractedStrings.length >= 4_000
          ? ["String index limit reached"]
          : []),
        ...(nestedClassBudget === 0
          ? ["Nested-class analysis budget reached"]
          : []),
      ],
    },
    benchmark: BENCHMARK_SUMMARY,
    totalUncompressed,
    compressionRatio:
      compressedTotal > 0
        ? Math.round((totalUncompressed / compressedTotal) * 10) / 10
        : 1,
    archiveBreakdown: archiveBreakdown(archiveEntries),
    warnings: [
      ...yaraWarnings,
      ...advisoryWarnings,
      ...(unsafePaths.length
        ? ["Unsafe paths were indexed but never written to disk."]
        : []),
      ...(containerReadWarning ? [containerReadWarning] : []),
      ...(classes.length < classEntries.length
        ? [
            `${classEntries.length - classes.length} class files could not be parsed.`,
          ]
        : []),
      ...(hashCandidates.length > topLevelHashedCount
        ? [
            `Entry hashing was bounded to ${formatBytes(MAX_ENTRY_HASH_BUDGET)} and ${topLevelHashedCount.toLocaleString()} top-level entries.`,
          ]
        : []),
      // Two different reasons a candidate went unopened, reported separately so
      // neither is folded into the other. The cap shortfall is its own line
      // because it used to be invisible: the candidate list was sliced before
      // this comparison, so exceeding the cap said nothing at all.
      ...(nestedCapSkipped > 0
        ? [
            `Nested-archive limit reached: ${nestedCapSkipped} of ${nestedCandidates.length} archives were not opened (limit ${MAX_NESTED_ARCHIVES}).`,
          ]
        : []),
      ...(nestedSkippedAfterCap > 0
        ? [
            `${nestedSkippedAfterCap} nested archives were malformed or exceeded nested-analysis limits.`,
          ]
        : []),
      "Static analysis does not execute the sample and cannot prove a file is safe.",
    ],
    analyzerVersion:
      `${ANALYZER_VERSION} · Intel ${THREAT_INTEL_VERSION} · ` +
      `${getThreatIntelCounts().total} hashes`,
  };

  reportProgress(onProgress, "Building report", 100, "Workspace ready");
  const classEntryByInternalName = new Map(
    classes.map((item) => [item.name.replaceAll(".", "/"), item.path]),
  );

  return {
    report,
    async readEntry(path: string) {
      const entry = archive.file(path);
      return entry ? entry.async("uint8array") : null;
    },
    async getClassBytes(internalName: string) {
      const normalized = internalName.replace(/\.class$/i, "").replaceAll(".", "/");
      const entry = archive.file(
        classEntryByInternalName.get(normalized) ?? `${normalized}.class`,
      );
      return entry ? entry.async("uint8array") : null;
    },
  };
}
